import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { GetTicketsByFiltersDtoProps } from "api/DTOs/GetTicketsByFiltersDto"
import { RemesaDeFacturaCreationDtoProps } from "api/DTOs/RemesaDeFacturaCreationDto"
import { createRemesaDeTicket } from "api/RemesaDeTicket.service"
import { TicketParamsProps, getTicketsByFilter } from "api/Ticket.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import axios from "axios"
import ActionToast from "common/components/ActionToast"
import { PAYMENT_TYPE } from "common/utils/constants"
import notification from "common/utils/notification"
import { RESP_200 } from "constants/numbers"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { DEFAULT_CURRENCY_SYMBOL } from "features/Contactos/constants"
import { ImporteBodyTemplate } from "features/Documentos/templates"
import { NewRemesaFormData } from "features/Tesoreria/components/NewRemesaForm"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import NewRemesaFormModal from "../Cartera/components/NewRemesaFormModal"
import { getIdsFromDocumentos, getTotals } from "../Cartera/constants"
import { FilterMenu } from "./components/FilterMenu"
import { REEMBOLSO_TICKETS_COLUMNS } from "./constants/constants"

const ReembolsoTickets = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const dtRef = useRef<DataTablePrimeReact<
    GetTicketsByFiltersDtoProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }
  const [documentSelected, setDocumentSelected] = useState<
    GetTicketsByFiltersDtoProps[]
  >([])
  const [showNewRemesa, setShowNewRemesa] = useState<{
    data: NewRemesaFormData | null
    status: boolean
  }>({
    data: null,
    status: false,
  })
  const [toastValues, setToastValues] = useState<{
    total: number | null
    quantity: number | null
  }>({ total: 0, quantity: 0 })

  const handleRedirectToList = () => {
    history.push(ROUTES.REMESAS)
  }

  const reembolsoTicketCancelToken = useRef(axios.CancelToken.source())
  const fetchItems = useCallback(() => {
    return (queryParams: TicketParamsProps) => {
      reembolsoTicketCancelToken.current?.cancel()
      reembolsoTicketCancelToken.current = axios.CancelToken.source()
      return getTicketsByFilter(
        {
          ...queryParams,
          EsReembolso: true,
          SkipCount: queryParams?.SkipCount ?? pagination.skipCount,
          MaxResultCount:
            queryParams?.MaxResultCount ?? pagination.itemsPerPage,
          SortingCriteria: queryParams.SortingCriteria,
        },
        reembolsoTicketCancelToken.current.token
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { items, isLoading, totalCount, setServiceQuery } = useFilterMenu({
    serviceCall: fetchItems,
  })

  const clearRemesaSelection = useCallback(() => {
    setDocumentSelected([])
    setToastValues({ total: 0, quantity: 0 })
    history.replace()
  }, [history])

  const onSuccessCreateRemesa = async () => {
    handleRedirectToList()
  }
  const onPostCreate = () => {
    clearRemesaSelection()
  }

  const handleCreateRemesa = async (
    values: RemesaDeFacturaCreationDtoProps
  ) => {
    try {
      const cleanedObj = Object.fromEntries(
        Object.entries(values).filter(([prop]) => prop !== "tipoFlujoTesoreria")
      )
      const { facturaIds, ...rest } = cleanedObj
      const modifiedValues = {
        ...rest,
        ticketIds: facturaIds ?? [],
      }
      const resp = await createRemesaDeTicket(modifiedValues)
      if (resp.status === RESP_200) {
        onPostCreate()
        onSuccessCreateRemesa()
        setShowNewRemesa({ data: null, status: false })
        notification.success({
          body: String(t("cartera.msg.createTextExist")),
        })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error creando remesa:", error)
    }
  }

  const handleSelected = (rowsSelected: GetTicketsByFiltersDtoProps[]) => {
    const totalValue = getTotals(rowsSelected, "importe")
    setToastValues({ total: totalValue, quantity: rowsSelected.length })
  }

  const handleOpenNewRemesa = (isOpen: boolean) => {
    const documentIds = getIdsFromDocumentos(documentSelected)
    setShowNewRemesa({
      data: {
        total: toastValues.total,
        facturasIds: documentIds,
        tipo: PAYMENT_TYPE.PAGO,
      },
      status: isOpen,
    })
  }

  const handleHideToast = () => {
    setDocumentSelected([])
    setToastValues({ total: null, quantity: null })
  }

  const onRenderContent = useMemo(() => {
    return (
      <div className='tw-flex tw-justify-between'>
        <span>
          {t("cartera.toast.selectedLabel")}{" "}
          <span className='tw-font-medium'>{toastValues?.quantity}</span>
        </span>
        <span className='tw-font-medium'>
          {t("cartera.toast.totalLabel")}{" "}
          <ImporteBodyTemplate
            importe={toastValues?.total ?? ""}
            divisa={DEFAULT_CURRENCY_SYMBOL}
          />
        </span>
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastValues])

  return (
    <>
      <section className='tw-flex tw-flex-col tw-order-2 tw-z-10 tw-relative tw-bg-white tw-w-full tw-max-h-screen tw-h-full tw-overflow-x-hidden tw-overflow-y-hidden'>
        <FilterMenu
          serviceQuery={setServiceQuery}
          maxResultCount={pagination.itemsPerPage}
          sortingCriteria={sortingCriteria}
          resetPagination={handleResetPagination}
          skipCount={pagination.skipCount}
        />
        <div className='tw-flex tw-flex-col tw-h-[calc(100%-85px)] tw-relative'>
          <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
            <TableERP
              refTable={dtRef}
              data-testid='tabla-reembolso-tickets'
              id='tabla-reembolso-tickets'
              className='tw-h-full !tw-z-0'
              dataKey='id'
              columns={REEMBOLSO_TICKETS_COLUMNS(t)}
              value={items}
              loading={isLoading}
              selection={documentSelected}
              selectionMode='checkbox'
              selectionColumnMode='multiple'
              onSelectionChange={({
                value,
              }: {
                value: GetTicketsByFiltersDtoProps[]
              }) => {
                setDocumentSelected(value)
                handleSelected(value)
              }}
              onRowClick={(e) => {
                const clickedItem = e.data
                setDocumentSelected((prevSelected) => {
                  const isSelected = prevSelected.some(
                    (item) => item.id === clickedItem.id
                  )
                  const newSelection = isSelected
                    ? prevSelected.filter((item) => item.id !== clickedItem.id)
                    : [...prevSelected, clickedItem]
                  handleSelected(newSelection)
                  return newSelection
                })
              }}
              size='small'
              onSort={onSort}
              sortField={sortField}
              sortOrder={sortOrder}
              removableSort
              showGridlines
              lazy
              scrollable
              emptyMessage={t("primeReact.table.emptyMessage")}
            />
            <PaginationERP
              className={PAGINATION_STYLES}
              totalRecords={totalCount}
              first={pagination.skipCount}
              rows={pagination.itemsPerPage}
              resetScroll={() => {
                dtRef.current && dtRef.current?.resetScroll()
              }}
              setPagination={setPagination}
              optionDropdown={[10, 20, 50, 100]}
              texts={{
                textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
                textCurrentPageReport: `${t("pagination.from")}`,
              }}
            />
          </div>
        </div>

        <ActionToast
          show={Boolean(toastValues?.total)}
          onConfirm={() => handleOpenNewRemesa(!!toastValues?.total)}
          onHide={handleHideToast}
          content={onRenderContent}
          buttonLabel={t("cartera.toast.remittanceLabel")}
        />

        <NewRemesaFormModal
          showNewRemesa={showNewRemesa}
          handleOpenNewRemesa={handleOpenNewRemesa}
          handleCreateRemesa={handleCreateRemesa}
        />
      </section>
    </>
  )
}

export default ReembolsoTickets
